import { memo } from 'react';
import { Bike, Clock, MapPin } from 'lucide-react';
import { formatCurrency, timeAgo } from '../../data/mockData';
import { DELIVERY_MODE, ORDER_STATUS, PAYMENT_METHOD_LABELS, formatSlotWindow } from '../../api/orders';
import { statusColors } from '../../theme';
import Button from '../../components/Button';
import styles from './OrderCard.module.css';

function OrderCard({
  order, onOpen, onConfirm, onCancel, onPack, onAssign, onUnassign,
  selected, selectDisabled, onToggleSelect, tripSize, busy,
}) {
  const accent = statusColors[order.status]?.fg;
  const selectable = order.status === ORDER_STATUS.PACKED && !!onToggleSelect;
  return (
    <button
      className={`${styles.card} ${selected ? styles.cardSelected : ''} ${order.status === ORDER_STATUS.OUT_FOR_DELIVERY ? styles.cardOutForDelivery : ''}`}
      style={{ '--accent': accent }}
      onClick={() => onOpen(order)}
    >
      <div className={styles.top}>
        <span className={styles.topLeft}>
          {selectable && (
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!selected}
              disabled={!selected && selectDisabled}
              title={!selected && selectDisabled ? 'A rider can carry up to 2 orders per trip' : 'Select for batch dispatch'}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect(order.id)}
              aria-label={`Select order ${order.id} for batch dispatch`}
            />
          )}
          <span className={styles.id}>{order.id}</span>
        </span>
        <span className={styles.time}>{timeAgo(order.placedAt)}</span>
      </div>

      {order.deliveryMode === DELIVERY_MODE.SCHEDULED && (
        <p className={styles.scheduleTag}>
          <Clock size={11} /> {formatSlotWindow(order.slotStart, order.slotEnd) || 'Scheduled'}
        </p>
      )}

      <p className={styles.customer}>{order.customer.name || 'Customer'}</p>
      {order.address && (
        <p className={styles.meta}>
          <MapPin size={11} /> {order.address.split(',')[0]}
        </p>
      )}

      <div className={styles.items}>
        {order.items.slice(0, 4).map((it, i) => (
          <span key={i} className={styles.itemChip} title={it.name}>{it.emoji || '🧺'}</span>
        ))}
        {order.items.length > 4 && <span className={styles.itemChip}>+{order.items.length - 4}</span>}
        <span className={styles.itemCount}>{order.items.length} item{order.items.length === 1 ? '' : 's'}</span>
      </div>

      <div className={styles.bottom}>
        <span className={styles.total}>{formatCurrency(order.total)}</span>
        <span className={styles.pay}>{PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}</span>
      </div>

      {order.rider && (
        <div className={styles.riderRow}>
          <Bike size={12} /> {order.rider.name}
          {tripSize > 1 && <span className={styles.tripBadge}>{tripSize} stops</span>}
        </div>
      )}

      {order.status === ORDER_STATUS.FAILED_DELIVERY && order.failureReason && (
        <p className={styles.failReason}>{order.failureReason}</p>
      )}

      <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
        {order.status === ORDER_STATUS.RECEIVED && (
          <>
            <Button size="sm" variant="primary" disabled={busy} onClick={() => onConfirm(order.id)}>Confirm</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(order.id)}>Cancel</Button>
          </>
        )}
        {order.status === ORDER_STATUS.CONFIRMED && (
          <>
            <Button size="sm" variant="primary" disabled={busy} onClick={() => onPack(order.id)}>Pack order</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel(order.id)}>Cancel</Button>
          </>
        )}
        {order.status === ORDER_STATUS.PACKED && (
          <Button size="sm" variant="primary" icon={Bike} disabled={busy} onClick={() => onAssign(order)}>Assign rider</Button>
        )}
        {/* OFD unassign disabled by design (API: cannot_unassign_in_transit)
        {order.status === ORDER_STATUS.OUT_FOR_DELIVERY && (
          <Button size="sm" variant="ghost" className={styles.unassignBtn} disabled={busy} onClick={() => onUnassign(order.id)}>Unassign rider</Button>
        )}
        */}
        {(order.status === ORDER_STATUS.DELIVERED || order.status === ORDER_STATUS.FAILED_DELIVERY) && order.customer.sub && (
          <span className={styles.viewOnly}>ID: {order.customer.sub}</span>
        )}
      </div>
    </button>
  );
}

export default memo(OrderCard);
